import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Calendar, Clock, Target, Brain, Loader2, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIStudyPlan = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    target_exam: '',
    current_level: 'beginner',
    weak_subjects: [],
    available_hours_per_day: 2,
    exam_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [studyPlan, setStudyPlan] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const examOptions = ['SSC CGL', 'SSC CHSL', 'IBPS PO', 'SBI PO', 'IBPS Clerk', 'SBI Clerk', 'RBI Grade B'];
  const subjectOptions = ['Quantitative Aptitude', 'Reasoning', 'English', 'General Awareness', 'Banking Awareness'];

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchSavedPlans();
  }, []);

  const fetchSavedPlans = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/ai/my-study-plans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setSavedPlans(data);
      setLoadingPlans(false);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setLoadingPlans(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      weak_subjects: prev.weak_subjects.includes(subject)
        ? prev.weak_subjects.filter(s => s !== subject)
        : [...prev.weak_subjects, subject]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.target_exam || !formData.exam_date || formData.weak_subjects.length === 0) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);
    setStudyPlan(null);

    try {
      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/ai/study-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate study plan');
      }

      setStudyPlan(data);
      fetchSavedPlans(); // Refresh saved plans
    } catch (error) {
      console.error('Study plan error:', error);
      alert('Failed to generate study plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <Brain className="mx-auto mb-4" size={48} />
          <h1 className="text-4xl font-bold mb-2">AI Study Plan Generator</h1>
          <p className="text-red-100 text-lg">Get a personalized study plan tailored to your exam goals</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-2xl">
                    <Target className="mr-2 text-red-600" size={24} />
                    Create Your Study Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Target Exam */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Exam <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="target_exam"
                        value={formData.target_exam}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                        required
                      >
                        <option value="">Select your target exam</option>
                        {examOptions.map(exam => (
                          <option key={exam} value={exam}>{exam}</option>
                        ))}
                      </select>
                    </div>

                    {/* Current Level */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Preparation Level
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        {['beginner', 'intermediate', 'advanced'].map(level => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, current_level: level }))}
                            className={`py-3 px-4 rounded-md border-2 font-medium capitalize transition-all ${
                              formData.current_level === level
                                ? 'border-red-600 bg-red-50 text-red-700'
                                : 'border-gray-300 hover:border-red-400'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Weak Subjects */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Weak Areas <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {subjectOptions.map(subject => (
                          <button
                            key={subject}
                            type="button"
                            onClick={() => handleSubjectToggle(subject)}
                            className={`py-2 px-4 rounded-full border-2 text-sm font-medium transition-all ${
                              formData.weak_subjects.includes(subject)
                                ? 'border-red-600 bg-red-600 text-white'
                                : 'border-gray-300 hover:border-red-400'
                            }`}
                          >
                            {subject}
                          </button>
                        ))}
                      </div>
                      {formData.weak_subjects.length === 0 && (
                        <p className="text-xs text-gray-500 mt-2">Select at least one subject</p>
                      )}
                    </div>

                    {/* Study Hours */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Study Hours Per Day
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          name="available_hours_per_day"
                          min="1"
                          max="12"
                          value={formData.available_hours_per_day}
                          onChange={handleInputChange}
                          className="flex-1"
                        />
                        <span className="text-2xl font-bold text-red-600 w-16 text-center">
                          {formData.available_hours_per_day}h
                        </span>
                      </div>
                    </div>

                    {/* Exam Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Exam Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="exam_date"
                        value={formData.exam_date}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-red-600 hover:bg-red-700 py-6 text-lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={20} />
                          Generating Your Plan...
                        </>
                      ) : (
                        <>
                          <Brain className="mr-2" size={20} />
                          Generate Study Plan
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Generated Plan Display */}
              {studyPlan && (
                <Card className="mt-8 border-2 border-green-500">
                  <CardHeader className="bg-green-50">
                    <CardTitle className="flex items-center text-2xl text-green-700">
                      <CheckCircle className="mr-2" size={24} />
                      Your Personalized Study Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="mb-4 p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <strong>Target Exam:</strong> {studyPlan.target_exam}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Generated For:</strong> {studyPlan.generated_for}
                      </p>
                    </div>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-800">
                        {studyPlan.study_plan}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Saved Plans */}
            <div>
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BookOpen className="mr-2 text-red-600" size={20} />
                    My Study Plans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingPlans ? (
                    <div className="text-center py-6">
                      <Loader2 className="animate-spin mx-auto text-red-600" size={24} />
                    </div>
                  ) : savedPlans.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">
                      No saved plans yet. Generate your first plan!
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {savedPlans.map((plan, idx) => (
                        <div
                          key={plan.id}
                          className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => setStudyPlan(plan)}
                        >
                          <p className="font-medium text-sm text-gray-900">{plan.target_exam}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(plan.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIStudyPlan;
